"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";

import { ShellLink } from "@/src/components";
import React from "react";
import { cn } from "utils";

export interface SectionHeaderProps {
  /** 标题文案或自定义节点 */
  title: React.ReactNode;
  /** 点击标题区域（标题 + chevron）的回调 */
  onClick?: (() => void) | undefined | null | false;
  /** 右侧可选插槽（如图例、按钮等） */
  rightSlot?: React.ReactNode;
  /** 如果提供 href，则使用 Link 包裹标题区域 */
  href?: string;
  /** 容器额外样式 */
  className?: string;
  /** 标题额外样式 */
  titleClassName?: string;
  /** 是否展示 chevron */
  showChevron?: boolean;
}

/**
 * 首页通用区块标题
 * - 左侧：标题 + 可选 chevron
 * - 右侧：可选插槽（图例/操作按钮）
 * - 用于 ThemesHeatmap 等首页模块的头部
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  rightSlot,
  href,
  onClick,
  className,
  titleClassName,
  showChevron = true,
}) => {
  const handleClick = () => {
    if (typeof onClick === "function") {
      onClick();
    }
  };

  const clickable = showChevron && (!!href || typeof onClick === "function");

  const TitleContent = (
    <div
      data-openreplay-label="Title Content"
      className={cn(
        "flex flex-row items-center",
        "max-md:w-full max-md:justify-between",
        clickable && "cursor-pointer select-none",
      )}
      onClick={!href && clickable ? handleClick : undefined}
    >
      <h2
        aria-label="Title Content h2"
        data-openreplay-label="Title Content h2 Semantic"
        id="sem-title-content-h2"
        className={cn(
          "headline_regular_s text-content_primary",
          clickable && "hover:text-content_secondary transition-colors",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {showChevron && (
        <span className="flex items-center justify-center w-6 h-6">
          <Icon
            name="chevron-right"
            size={20}
            color={ospThemes.colors.content_tertiary}
          />
        </span>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "flex flex-row justify-between items-center mb-5 md:mb-4",
        "max-md:w-full",
        className,
      )}
    >
      {href && clickable ? (
        <ShellLink
          aria-label="Title Content"
          data-openreplay-label={`Title Content Link | ${href}`}
          id="lnk-title-content"
          href={href}
          className="no-underline text-current block max-md:w-full"
        >
          {TitleContent}
        </ShellLink>
      ) : (
        TitleContent
      )}

      {rightSlot && <div className="flex items-center">{rightSlot}</div>}
    </div>
  );
};
